// src/pages/services/Forex.js
import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import '../../styles/ServicePage.css';
import AnimatedBackground from '../../components/AnimatedBackground';

const Forex = () => {
    const fadeIn = {
        initial: { opacity: 0, y: 20 },
        animate: { opacity: 1, y: 0 },
        transition: { duration: 0.6 }
    };

    const testimonials = [
        {
            content: "The forex service at Excellent Travels saved me a significant amount on my European vacation. Their rates were much better than my bank, and the process was quick and convenient.",
            name: "Richard Taylor",
            location: "San Francisco, USA",
            avatar: "/images/testimonials/person15.jpg"
        },
        {
            content: "I was able to order multiple currencies for my world tour with just one application. The home delivery option was incredibly convenient, and the rates were very competitive.",
            name: "Sophia Wong",
            location: "Hong Kong",
            avatar: "/images/testimonials/person16.jpg"
        }
    ];

    const faqs = [
        {
            question: "What currencies do you offer?",
            answer: "We offer over 50 major and exotic currencies including USD, EUR, GBP, JPY, AUD, CAD, CHF, SGD, AED, THB, and many more. If you need a specific currency not listed on our website, please contact us as we can often source it with advance notice."
        },
        {
            question: "How do I get the best exchange rate?",
            answer: "To secure the best rates, we recommend pre-ordering your currency 3-5 days before you need it. Our rates are updated daily based on market conditions. You can also sign up for our rate alert service to be notified when your desired currency reaches your target exchange rate."
        },
        {
            question: "Is there a minimum or maximum amount I can exchange?",
            answer: "For most currencies, our minimum exchange amount is equivalent to $100 USD. Maximum amounts vary by currency and availability, but we can typically accommodate large exchanges with advance notice. For very large amounts, we offer special rates and personalized service."
        },
        {
            question: "Do you offer delivery services?",
            answer: "Yes, we offer secure home or office delivery for foreign currency orders over $500 USD equivalent. For smaller amounts or immediate needs, you can pick up your currency at any of our branch locations. Delivery is typically made within 1-2 business days after your order is processed."
        }
    ];


    // Add useEffect to ensure proper scrolling
    useEffect(() => {
        // Enable scrolling when component mounts
        document.body.style.overflow = 'auto';
        document.documentElement.style.overflow = 'auto';

        // Cleanup function to ensure scrolling is enabled when component unmounts
        return () => {
            document.body.style.overflow = 'auto';
            document.documentElement.style.overflow = 'auto';
        };
    }, []);
    return (
        <motion.div
            className="service-page"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
        >
            {/* Add animated background */}
            <AnimatedBackground type="forex" />

            {/* Main content */}
            <div className="service-hero">
                <img
                    src="https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                    alt="Foreign Exchange Services"
                    className="service-hero-image"
                />
                <div className="service-hero-overlay">
                    <h1 className="service-title">Foreign Exchange Services</h1>
                    <p className="service-subtitle">
                        Competitive foreign exchange rates for all major currencies.
                    </p>
                </div>
            </div>

            {/* Service Logo */}
            <img
                src="https://cdn-icons-png.flaticon.com/512/2933/2933266.png"
                alt="Forex Logo"
                className="service-logo"
            />

            <div className="service-content">
                <motion.div
                    className="service-description"
                    {...fadeIn}
                >
                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2933/2933266.png" alt="Forex Icon" className="section-icon" />
                        Travel with the Right Currency at the Right Rate
                    </h2>
                    <p>
                        At Excellent Travels, we understand that having access to the right currency at competitive rates
                        is an essential part of your travel planning. Our foreign exchange services are designed to provide
                        you with convenient access to global currencies at excellent rates.
                    </p>
                    <p>
                        Our forex services include:
                    </p>
                    <ul>
                        <li>Exchange services for over 50 major and exotic currencies</li>
                        <li>Competitive rates updated daily based on market conditions</li>
                        <li>Zero commission on most currency exchanges</li>
                        <li>Secure home or office delivery options</li>
                        <li>Multi-currency prepaid travel cards</li>
                        <li>Currency buyback service for unused travel money</li>
                    </ul>

                    <div className="feature-image-container">
                        <img
                            src="https://images.unsplash.com/photo-1580048915913-4f8f5cb481c4?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80"
                            alt="Foreign Currency Exchange"
                            className="feature-image"
                        />
                        <div className="feature-image-caption">Access to over 50 global currencies at competitive exchange rates</div>
                    </div>

                    <p>
                        Whether you're planning a vacation, business trip, or sending money to family abroad,
                        our forex specialists can help you navigate the complexities of international currency exchange.
                    </p>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2933/2933087.png" alt="Forex Products Icon" className="section-icon" />
                        Our Foreign Exchange Products
                    </h2>
                    <p>
                        We offer various forex solutions to meet your travel needs:
                    </p>
                    <ul>
                        <li><strong>Cash Currency Exchange:</strong> Physical foreign currency for immediate use upon arrival</li>
                        <li><strong>Multi-Currency Travel Cards:</strong> Secure, convenient cards that hold multiple currencies</li>
                        <li><strong>Traveler's Cheques:</strong> Traditional secure payment method accepted worldwide</li>
                        <li><strong>International Money Transfers:</strong> Send money abroad quickly and securely</li>
                        <li><strong>Currency Forward Contracts:</strong> Lock in today's exchange rate for future travel</li>
                    </ul>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2933/2933116.png" alt="Benefits Icon" className="section-icon" />
                        Benefits of Our Forex Services
                    </h2>
                    <p>
                        Choosing our foreign exchange services offers several advantages:
                    </p>
                    <ul>
                        <li><strong>Better Rates:</strong> Our volume purchasing power means we can offer more competitive rates than most banks</li>
                        <li><strong>Convenience:</strong> Order online, by phone, or in person with multiple collection/delivery options</li>
                        <li><strong>Expert Advice:</strong> Our forex specialists can advise on currency trends and optimal exchange timing</li>
                        <li><strong>Security:</strong> All transactions are secure and compliant with international regulations</li>
                        <li><strong>Comprehensive Service:</strong> From pre-trip currency to post-trip buyback, we handle all your forex needs</li>
                    </ul>

                    <h2>
                        <img src="https://cdn-icons-png.flaticon.com/512/2784/2784403.png" alt="Process Icon" className="section-icon" />
                        How It Works
                    </h2>
                    <p>
                        Exchanging currency with us is simple:
                    </p>
                    <ol>
                        <li>Check our daily rates online or contact our forex team</li>
                        <li>Place your order specifying the currencies and amounts needed</li>
                        <li>Choose your preferred delivery or collection method</li>
                        <li>Complete your payment securely</li>
                        <li>Receive your foreign currency when and where you need it</li>
                        <li>After your trip, exchange unused currency back at competitive rates</li>
                    </ol>
                </motion.div>

                <motion.div
                    className="service-sidebar"
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                >
                    <img
                        src="https://cdn-icons-png.flaticon.com/512/2933/2933266.png"
                        alt="Forex Icon"
                        className="service-sidebar-logo"
                    />

                    <h3>Why Choose Our Forex Services</h3>
                    <ul className="service-features">
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933266.png" alt="No Fees Icon" />
                            </span>
                            No hidden fees or charges
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933116.png" alt="Better Rates Icon" />
                            </span>
                            Better rates than most banks
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2933/2933087.png" alt="Currencies Icon" />
                            </span>
                            Over 50 currencies available
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921222.png" alt="Delivery Icon" />
                            </span>
                            Secure home delivery option
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921048.png" alt="Lock Rate Icon" />
                            </span>
                            Rate lock guarantee
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2784/2784389.png" alt="Expert Advice Icon" />
                            </span>
                            Expert currency advice
                        </li>
                        <li>
                            <span className="feature-icon">
                                <img src="https://cdn-icons-png.flaticon.com/512/2921/2921195.png" alt="Buyback Icon" />
                            </span>
                            Buyback service for unused currency
                        </li>
                    </ul>
                </motion.div>
            </div>

            <motion.div
                className="cta-section"
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
            >
                <h2>Ready to Exchange Currency?</h2>
                <p>
                    Contact our forex specialists today to discuss your currency needs and receive our competitive exchange rates for your travel money.
                </p>
                <Link to="/contact" className="cta-button">
                    Request Current Rates
                    <span className="arrow-icon">→</span>
                </Link>
            </motion.div>

            <motion.div
                className="testimonials"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.6 }}
            >
                <h2>What Our Customers Say</h2>
                <div className="testimonials-grid">
                    {testimonials.map((testimonial, index) => (
                        <motion.div
                            key={index}
                            className="testimonial-card"
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5, delay: 0.7 + (index * 0.1) }}
                        >
                            <p className="testimonial-content">"{testimonial.content}"</p>
                            <div className="testimonial-author">
                                <div className="author-info">
                                    <h4>{testimonial.name}</h4>
                                    <p>{testimonial.location}</p>
                                </div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <motion.div
                className="faq-section"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
            >
                <h2>Frequently Asked Questions</h2>
                {faqs.map((faq, index) => (
                    <motion.div
                        key={index}
                        className="faq-item"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}
                    >
                        <h3 className="faq-question">{faq.question}</h3>
                        <p className="faq-answer">{faq.answer}</p>
                    </motion.div>
                ))}
            </motion.div>
        </motion.div>
    );
};

export default Forex;